import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { ActionLock, IdemKey } from './core.js';
/**
 * One lock for one action. `onError` is the ONLY error channel — run() never
 * rejects into the caller, so a screen shows at most one failure per settle.
 */
export function useActionLock(onError) {
    const lock = useMemo(() => new ActionLock(), []);
    return useBoundLock(lock, onError);
}
/**
 * One lock shared by several mutually-exclusive actions (e.g. the stop
 * ladder: set off / arrive / complete / problem). While any bound action is
 * in flight, every bound action is locked.
 */
export function useActionLockGroup() {
    return useMemo(() => new ActionLock(), []);
}
/** Bind a shared group lock into the same {locked, run} shape. */
export function useBoundLock(lock, onError) {
    const locked = useSyncExternalStore(lock.subscribe, () => lock.locked, () => lock.locked);
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    const run = useCallback((fn) => {
        lock.run(fn, (err) => onErrorRef.current?.(err));
    }, [lock]);
    return { locked, run };
}
/**
 * Idempotency key per action instance. `scope` exists only to reset the key
 * when the target changes (e.g. a new stopId): same scope → same key across
 * renders, taps, and retries; new scope → fresh key.
 */
export function useIdemKey(scope) {
    const ref = useRef(null);
    if (ref.current === null || ref.current.scope !== scope) {
        ref.current = { scope, holder: new IdemKey() };
    }
    const holder = ref.current.holder;
    const rotate = useCallback(() => holder.rotate(), [holder]);
    return { key: holder.key, rotate };
}
//# sourceMappingURL=react.js.map